import * as React from 'react';
import { Dialog } from 'primereact/dialog';

import { ProjectDocFolder } from '../../models/ProjectDocFolder';
import { NewDropdown } from '../../../../components/synced/ui/NewDropdown/NewDropdown';
import { AddActModel } from '../../services/ProjectDocFolderService';
import { useBackgroundPromiseEffectByCondition } from '../../../../helpers/hooks/useBackgroundPromiseEffectByCondition';
import { services } from '../../../../services';
import { constantFormat } from '../../../../helpers/constants';
import { NewInput } from '../../../../components/synced/ui/NewInput/NewInput';
import { NewCalendar } from '../../../../components/synced/ui/NewCalendar/NewCalendar';
import { NewButton } from '../../../../components/synced/ui/NewButton/NewButton';

interface AddActDialogProps {
    visible: boolean;
    folder: ProjectDocFolder;
    docTypeId: number;
    onSave: (data?: AddActModel) => void;
    onCancel: () => void;
}

export const AddActDialog: React.FC<AddActDialogProps> = ({ visible, folder, docTypeId, onSave, onCancel }) => {
    const [data, setData] = React.useState<AddActModel>({} as AddActModel);
    React.useEffect(() => setData({ ProjectDocFolderId: folder.Id } as AddActModel), [folder]);

    const isDataValid = React.useCallback((): boolean => {
        return (
            Boolean(data?.ActTemplateId) &&
            data?.Title?.trim()?.length > 0 &&
            data?.Cypher?.trim()?.length > 0 &&
            Boolean(data?.DocumentDate)
        );
    }, [data]);

    const [optionsActTemplate, setOptionsActTemplate] = React.useState<any[]>([]);
    useBackgroundPromiseEffectByCondition(
        () => Boolean(folder?.Id),
        () => services.projectDocFolderService.getAvailableActTemplates(folder.Id!),
        (res: any[]) => setOptionsActTemplate(res),
        [folder]
    );

    const footer = (
        <div className="pd-dialog-footer">
            <NewButton variant="secondary" icon="fa-solid fa-xmark" onClick={onCancel}>
                Отменить
            </NewButton>
            <NewButton
                variant="primary"
                icon="fa-solid fa-check"
                disabled={!isDataValid()}
                onClick={() => onSave(data)}
            >
                Сохранить
            </NewButton>
        </div>
    );

    return (
        <React.Fragment>
            <Dialog
                style={{ width: '50vw' }}
                footer={footer}
                focusOnShow={false}
                contentStyle={{ overflow: `visible` }}
                className="pd-dialog"
                header="Добавление акта"
                visible={visible}
                onHide={onCancel}
            >
                <div className="pd-dialog-body">
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">Шаблон акта</span>
                        <NewDropdown
                            className="w-100"
                            value={data?.ActTemplateId}
                            options={optionsActTemplate}
                            onChange={(e) => {
                                const template = optionsActTemplate.find((o: { Id: number }) => o.Id === e.value);
                                setData({
                                    ...data,
                                    ActTemplateId: e.value,
                                    Title: template?.Title ?? data?.Title ?? ''
                                });
                            }}
                            appendTo={document.body}
                            optionLabel="Title"
                            optionValue="Id"
                        />
                        {!data.ActTemplateId && <small className="pd-field-error">Укажите значение</small>}
                    </div>
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">Название</span>
                        <NewInput
                            type="text"
                            className="w-100"
                            placeholder="Введите название"
                            value={data.Title}
                            onChange={(e) => setData({ ...data, Title: e.currentTarget.value })}
                        />
                        {(!data.Title || !data.Title?.trim()) && (
                            <small className="pd-field-error">Укажите значение</small>
                        )}
                    </div>
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">Номер</span>
                        <NewInput
                            type="text"
                            className="w-100"
                            placeholder="Введите номер"
                            value={data.Cypher}
                            onChange={(e) => setData({ ...data, Cypher: e.currentTarget.value })}
                        />
                        {(!data.Cypher || !data.Cypher?.trim()) && (
                            <small className="pd-field-error">Укажите значение</small>
                        )}
                    </div>
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">Дата документа</span>
                        <NewCalendar
                            keepInViewport
                            appendTo={document.body}
                            locale="ru"
                            dateFormat={constantFormat.calendar}
                            className="w-100"
                            value={constantFormat.StringToDate(data.DocumentDate as string)}
                            onChange={(e) => setData({ ...data, DocumentDate: e.value as Date })}
                        />
                        {!data.DocumentDate && <small className="pd-field-error">Укажите значение</small>}
                    </div>
                </div>
            </Dialog>
        </React.Fragment>
    );
};
